import React, { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { mensajeAmigable } from '../utils/errores';
import { limpiarTexto } from '../utils/validacion';
import { obtenerClientes } from '../services/clientesService';
import { obtenerServicios } from '../services/serviciosService';
import { subirFoto } from '../services/fotosService';
import { crearOrden, actualizarOrden } from '../services/ordenesService';
import { ESTADOS_ORDEN } from '../utils/ordenes';
import { optimizarFoto } from '../utils/imagen';

const P = 'var(--color-primario,#DB2777)';
const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const label = { display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 600, color: '#374151' };

// `inicial` = datos precargados al CREAR (ej. desde una cotización aprobada).
export default function OrdenForm({ orden = null, inicial = null, onCerrar, onGuardado }) {
  const editando = !!orden;
  const base = orden || inicial || {};
  const { mostrarToast } = useToast();
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  const [clienteId, setClienteId] = useState(base.cliente_id || '');
  const [clienteNombre, setClienteNombre] = useState(base.cliente_nombre || '');
  const [servicioId, setServicioId] = useState(base.servicio_id || '');
  const [estado, setEstado] = useState(base.estado || 'pendiente');
  const [fechaInicio, setFechaInicio] = useState(base.fecha_inicio || '');
  const [fechaEntrega, setFechaEntrega] = useState(base.fecha_entrega || '');
  const [notas, setNotas] = useState(base.notas || '');
  const [evidencias, setEvidencias] = useState(Array.isArray(base.evidencias) ? base.evidencias : []);

  useEffect(() => {
    obtenerClientes().then(setClientes).catch(() => {});
    obtenerServicios().then(setServicios).catch(() => {});
  }, []);

  const elegirCliente = (id) => {
    setClienteId(id);
    if (id) { const c = clientes.find((x) => x.id === id); setClienteNombre(c?.nombre || ''); }
  };

  // Sube las fotos de evidencia elegidas (varias a la vez) y las agrega.
  const agregarEvidencias = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    setSubiendo(true);
    try {
      const urls = [];
      for (const f of files) { urls.push(await subirFoto(f)); } // eslint-disable-line no-await-in-loop
      setEvidencias((prev) => [...prev, ...urls]);
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); }
    finally { setSubiendo(false); }
  };
  const quitarEvidencia = (i) => setEvidencias((prev) => prev.filter((_, j) => j !== i));

  const guardar = async () => {
    if (!clienteId && !clienteNombre.trim() && !servicioId) {
      mostrarToast('Pon al menos el cliente o el servicio de la orden', 'error'); return;
    }
    setGuardando(true);
    try {
      const serv = servicios.find((s) => s.id === servicioId);
      const datos = {
        cliente_id: clienteId || null,
        cliente_nombre: clienteNombre.trim() || null,
        servicio_id: servicioId || null,
        servicio_nombre: serv?.nombre || base.servicio_nombre || null,
        estado,
        fecha_inicio: fechaInicio || null,
        fecha_entrega: fechaEntrega || null,
        notas: notas.trim() || null,
        evidencias,
        cotizacion_id: base.cotizacion_id || null,
      };
      if (editando) await actualizarOrden(orden.id, datos);
      else await crearOrden(datos);
      mostrarToast(editando ? 'Orden actualizada ✅' : 'Orden creada 📋', 'exito');
      onGuardado && onGuardado();
      onCerrar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
      setGuardando(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '14px' }} onClick={guardando ? undefined : onCerrar}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '22px', width: '460px', maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: P, fontSize: '19px' }}>{editando ? `✏️ ${orden.numero || 'Orden'}` : '📋 Nueva orden'}</h2>
          <button onClick={onCerrar} disabled={guardando} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
        </div>

        <label style={label}>Cliente</label>
        <select value={clienteId} disabled={guardando} onChange={(e) => elegirCliente(e.target.value)} style={{ ...input, marginBottom: '8px' }}>
          <option value="">— Sin cliente / escribir abajo —</option>
          {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
        {!clienteId && (
          <input value={clienteNombre} disabled={guardando} maxLength={80} placeholder="O escribe el nombre del cliente"
            onChange={(e) => setClienteNombre(limpiarTexto(e.target.value, 80))} style={{ ...input, marginBottom: '12px' }} />
        )}
        {clienteId && <div style={{ marginBottom: '12px' }} />}

        <label style={label}>Servicio (opcional)</label>
        <select value={servicioId} disabled={guardando} onChange={(e) => setServicioId(e.target.value)} style={{ ...input, marginBottom: '12px' }}>
          <option value="">— Ninguno —</option>
          {servicios.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
        </select>

        <label style={label}>Estado</label>
        <select value={estado} disabled={guardando} onChange={(e) => setEstado(e.target.value)} style={{ ...input, marginBottom: '12px' }}>
          {ESTADOS_ORDEN.map((e) => <option key={e.clave} value={e.clave}>{e.nombre}</option>)}
        </select>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          <div>
            <label style={label}>Fecha de inicio</label>
            <input type="date" value={fechaInicio} disabled={guardando} onChange={(e) => setFechaInicio(e.target.value)} style={input} />
          </div>
          <div>
            <label style={label}>Fecha de entrega</label>
            <input type="date" value={fechaEntrega} disabled={guardando} onChange={(e) => setFechaEntrega(e.target.value)} style={input} />
          </div>
        </div>

        <label style={label}>Nota (opcional)</label>
        <textarea value={notas} disabled={guardando} rows={2} maxLength={800}
          onChange={(e) => setNotas(e.target.value)} style={{ ...input, fontFamily: 'inherit', resize: 'vertical', marginBottom: '12px' }}
          placeholder="Detalle del trabajo, indicaciones…" />

        {/* Evidencias (fotos del trabajo) */}
        <label style={label}>📷 Evidencias (fotos del trabajo)</label>
        {evidencias.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
            {evidencias.map((u, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={optimizarFoto(u, 160)} alt="" style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                <button type="button" onClick={() => quitarEvidencia(i)} disabled={guardando}
                  style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', border: 'none', background: '#B91C1C', color: '#fff', fontSize: '11px', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
            ))}
          </div>
        )}
        <input type="file" accept="image/*" multiple disabled={guardando || subiendo}
          onChange={(e) => { agregarEvidencias(e.target.files); e.target.value = ''; }}
          style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '16px' }} />
        {subiendo && <p style={{ margin: '-8px 0 12px', fontSize: '12.5px', color: '#6B7280' }}>⏳ Subiendo fotos…</p>}

        <button onClick={guardar} disabled={guardando || subiendo}
          style={{ width: '100%', padding: '14px', background: (guardando || subiendo) ? '#9CA3AF' : P, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: guardando ? 'wait' : 'pointer' }}>
          {guardando ? '⏳ Guardando…' : (editando ? 'Guardar cambios' : 'Crear orden')}
        </button>
      </div>
    </div>
  );
}
